import { h } from 'preact';
import util from 'preact-util';
import { Text } from 'preact-i18n';

/**
 * Presentational product card shared by the convention member-products list and
 * the cart "add more" upsell row. Stateless: the `onAddToCart(product)` callback
 * is supplied by the parent, which owns add-to-cart behaviour and the
 * `conventionId` to attach.
 */
const MemberProductCard = ({ product, darkmode, cart = [], addedToCart = {}, convention, compact, onAddToCart }) => {
    const hasImage = product.images?.length > 0;
    const imageUrl = product.images?.[0]?.s3MediumLink;
    const isOutOfStock = product.inStock !== undefined && product.inStock <= 0;
    const inCart = cart.find(item => item.product === product.id);
    const justAdded = addedToCart[product.id];
    const priceWithVat = product.price * (1 + (product.vat || 0) / 100);
    const hasDiscount = product.discountAmount > 0;
    const discountedPrice = priceWithVat - (product.discountAmount || 0);
    const isSignupProduct = convention?.signupProduct > 0 && product.id === convention.signupProduct;

    const wrapClass = compact
        ? ''
        : 'col-6 col-md-4 col-lg-3';
    const wrapStyle = compact
        ? 'flex: 0 0 140px; min-width: 140px;'
        : '';
    const imageStyle = compact
        ? 'aspect-ratio: 1 / 1; overflow: hidden; background: #f0f0f0;'
        : 'aspect-ratio: 1 / 1; overflow: hidden; background: #f0f0f0;';
    const titleStyle = compact
        ? 'font-size: 0.75rem; font-weight: 600; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;'
        : 'font-size: 0.85rem; font-weight: 600; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;';
    const priceSize = compact ? '0.85rem' : '1rem';
    const btnClass = compact
        ? `btn btn-sm ${inCart || justAdded ? 'btn-success' : 'btn-primary'} w-100 mt-1 rounded-pill`
        : `btn btn-sm ${inCart || justAdded ? 'btn-success' : 'btn-primary'} w-100 mt-2 rounded-pill`;

    const handleAddToCart = (e) => {
        e.stopPropagation();
        if (typeof onAddToCart === 'function') {
            onAddToCart(product);
        }
    };

    return (
        <div class={wrapClass} style={wrapStyle} key={product.id}>
            <div
                class={`card h-100 ${darkmode ? 'bg-darkmode border-secondary' : ''}`}
                style={`overflow: hidden; ${isOutOfStock ? '' : 'cursor: pointer;'}`}
                role={isOutOfStock ? undefined : 'button'}
                onClick={isOutOfStock ? undefined : handleAddToCart}
            >
                {/* Image */}
                <div class='position-relative' style={imageStyle}>
                    {hasImage ? (
                        <img
                            src={imageUrl}
                            alt={product.name}
                            class='w-100 h-100'
                            style={`object-fit: cover; ${isOutOfStock ? 'opacity: 0.5;' : ''}`}
                        />
                    ) : (
                        <div class={`w-100 h-100 d-flex align-items-center justify-content-center ${darkmode ? 'bg-darkmode' : ''}`}>
                            <i class={`fa-duotone fa-box ${compact ? 'fa-2x' : 'fa-3x'} text-muted`} style='opacity: 0.3;' />
                        </div>
                    )}

                    {/* Out of stock overlay */}
                    {isOutOfStock && (
                        <div
                            class='position-absolute d-flex align-items-center justify-content-center'
                            style='top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.4);'
                        >
                            <span class='badge bg-secondary' style={`font-size: ${compact ? '0.7rem' : '0.9rem'};`}>
                                <Text id='product.out-of-stock'>Out of stock</Text>
                            </span>
                        </div>
                    )}

                    {/* Sale badge */}
                    {hasDiscount && (
                        <div class='position-absolute' style='top: 4px; left: 4px;'>
                            <span class='badge bg-danger' style={compact ? 'font-size: 0.65rem;' : ''}>
                                <i class='fa-solid fa-tag me-1' />
                                <Text id='product.sale'>Sale</Text>
                            </span>
                        </div>
                    )}

                    {/* In cart indicator */}
                    {inCart && (
                        <div class='position-absolute' style='top: 4px; right: 4px;'>
                            <span class='badge bg-success rounded-circle' style={`display: flex; align-items: center; justify-content: center; ${compact ? 'width: 22px; height: 22px;' : 'width: 28px; height: 28px;'}`}>
                                <i class='fa-solid fa-check' />
                            </span>
                        </div>
                    )}

                    {/* Signup product badge */}
                    {isSignupProduct && !compact && (
                        <div class='position-absolute' style='bottom: 8px; left: 8px;'>
                            <span class='badge bg-info'>
                                <i class='fa-solid fa-user-check me-1' />
                                <Text id='convention.signup-fee'>Signup fee</Text>
                            </span>
                        </div>
                    )}
                </div>

                {/* Info */}
                <div class={`card-body ${compact ? 'p-1' : 'p-2'} d-flex flex-column`}>
                    <h6 class='card-title mb-1' style={titleStyle}>
                        {product.name || <Text id='product.unnamed'>Unnamed product</Text>}
                    </h6>

                    {/* Price */}
                    <div class='mt-auto'>
                        {hasDiscount ? (
                            <div>
                                <span class='text-muted text-decoration-line-through' style='font-size: 0.65rem;'>
                                    kr {util.format(priceWithVat, 0)}
                                </span>
                                <span class='text-danger fw-bold ms-1' style={`font-size: ${priceSize};`}>
                                    kr {util.format(discountedPrice, 0)}
                                </span>
                            </div>
                        ) : (
                            <span class='fw-bold' style={`font-size: ${priceSize};`}>
                                kr {util.format(priceWithVat, 0)}
                            </span>
                        )}
                    </div>

                    {/* Add to cart button */}
                    {!isOutOfStock && (
                        <button
                            class={btnClass}
                            data-id={product.id}
                            onClick={handleAddToCart}
                            style={compact ? 'font-size: 0.7rem; padding: 0.2rem 0.5rem;' : ''}
                        >
                            {inCart || justAdded ? (
                                <>
                                    <i class='fa-solid fa-check me-1' />
                                    {!compact && <Text id='product.in-cart'>In cart</Text>}
                                </>
                            ) : (
                                <>
                                    {compact && <i class='fa-solid fa-plus me-1' />}
                                    <i class='fa-solid fa-cart-plus me-1' />
                                    {!compact && <Text id='product.add-to-cart'>Add to cart</Text>}
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MemberProductCard;
